import React, { useState, useEffect } from 'react';
import { UsageRecord, UserState } from '../types';
import { DollarSign, Activity, Server, Database, ShieldAlert, Users, CheckCircle, XCircle, Brain, Play, Loader, Filter, Shield } from 'lucide-react';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, limit, doc, updateDoc, addDoc } from 'firebase/firestore';
import { generateRawScenarioContent } from '../services/geminiService';

const AdminDashboard: React.FC = () => {
  const [records, setRecords] = useState<UsageRecord[]>([]);
  const [users, setUsers] = useState<UserState[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  
  // Generation State
  const [genCount, setGenCount] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState('');

  // Fetch Logic split into independent functions to prevent one failure blocking the other
  const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const fetchedUsers: UserState[] = [];
        usersSnapshot.forEach((doc) => {
            const data = doc.data() as UserState;
            fetchedUsers.push({ ...data, uid: doc.id });
        });
        setUsers(fetchedUsers);
      } catch (error) {
          console.error("Error fetching users:", error);
      }
  };

  const fetchLogs = async () => {
      try {
        const logsQ = query(collection(db, 'usage_logs'), orderBy('timestamp', 'desc'), limit(100));
        const logsSnapshot = await getDocs(logsQ);
        const logs: UsageRecord[] = [];
        logsSnapshot.forEach((doc) => {
            logs.push(doc.data() as UsageRecord);
        });
        setRecords(logs);
      } catch (error) {
          console.error("Error fetching logs:", error);
      }
  };

  const initData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchLogs()]);
      setLoading(false);
  };

  useEffect(() => {
    initData();
  }, []);

  const toggleApproval = async (user: UserState) => {
    if (!user.uid) return;
    try {
        const newStatus = !user.isApproved;
        await updateDoc(doc(db, 'users', user.uid), {
            isApproved: newStatus
        });
        // Update local state to reflect change immediately
        setUsers(users.map(u => u.uid === user.uid ? { ...u, isApproved: newStatus } : u));
    } catch (error) {
        console.error("Error updating user:", error);
        alert("Failed to update user status");
    }
  };

  const toggleRole = async (user: UserState) => {
      if (!user.uid) return;
      if (user.email === 'mkajanth@gmail.com') {
          alert("Cannot change role of Super Admin.");
          return;
      }
      
      const newRole = user.role === 'admin' ? 'user' : 'admin';
      
      if (confirm(`Are you sure you want to change ${user.name}'s role to ${newRole.toUpperCase()}?`)) {
          try {
              await updateDoc(doc(db, 'users', user.uid), {
                  role: newRole
              });
              setUsers(users.map(u => u.uid === user.uid ? { ...u, role: newRole } : u));
          } catch (error) {
              console.error("Error updating role:", error);
              alert("Failed to update role.");
          }
      }
  };

  const handleBatchGenerate = async () => {
      if (genCount <= 0) return;
      setIsGenerating(true);
      setGenStatus(`Starting batch generation of ${genCount} scenarios...`);

      try {
          for (let i = 0; i < genCount; i++) {
              setGenStatus(`Generating ${i + 1} of ${genCount}...`);
              
              // 1. Call Gemini Raw
              const content = await generateRawScenarioContent();
              
              // 2. Save to Cached Scenarios Collection
              await addDoc(collection(db, 'cached_scenarios'), content);
          }
          setGenStatus(`Success! Generated ${genCount} new scenarios.`);
      } catch (e) {
          console.error(e);
          setGenStatus(`Error during generation: ${e}`);
      } finally {
          setIsGenerating(false);
          setGenCount(0);
      }
  };

  const totalCost = records.reduce((acc, curr) => acc + curr.cost, 0);
  const totalCalls = records.length;
  
  const pendingCount = users.filter(u => !u.isApproved).length;
  const filteredUsers = showPendingOnly ? users.filter(u => !u.isApproved) : users;

  return (
    <div className="max-w-6xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 pt-10 border-b border-nunchi-200 pb-6 flex justify-between items-center">
        <div>
           <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-slate-600 text-xs font-bold uppercase tracking-wider mb-2">
              <ShieldAlert size={12} /> Admin Area
           </div>
           <h1 className="text-3xl font-serif font-bold text-slate-900">Control Center</h1>
           <p className="text-slate-600">Manage access, roles, and track API usage.</p>
        </div>
        <button onClick={initData} className="text-sm text-slate-500 hover:text-slate-800 underline">Refresh Data</button>
      </header>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center">
            <DollarSign size={24} />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-400 uppercase tracking-widest">Est. Total Cost</div>
            <div className="text-3xl font-bold text-emerald-700">${totalCost.toFixed(6)}</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
            <Users size={24} />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-400 uppercase tracking-widest">Total Users</div>
            <div className="text-3xl font-bold text-blue-700">{users.length}</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex items-center gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${pendingCount > 0 ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>
            <ShieldAlert size={24} />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-400 uppercase tracking-widest">Pending Approvals</div>
            <div className={`text-3xl font-bold ${pendingCount > 0 ? 'text-amber-600' : 'text-slate-700'}`}>{pendingCount}</div>
          </div>
        </div>
      </div>

      <div className="space-y-12">
        
        {/* User Management */}
        <section>
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                    <Users size={20} /> User Access Control
                </h3>
                <button 
                    onClick={() => setShowPendingOnly(!showPendingOnly)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                        showPendingOnly ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                >
                    <Filter size={16} /> 
                    {showPendingOnly ? 'Show All Users' : `Filter Pending (${pendingCount})`}
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-xs">
                            <tr>
                                <th className="px-6 py-4">Name / Email</th>
                                <th className="px-6 py-4">Joined</th>
                                <th className="px-6 py-4">Seen</th>
                                <th className="px-6 py-4">Role</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredUsers.length === 0 ? (
                                <tr><td colSpan={6} className="p-8 text-center text-slate-500">No users found matching filter.</td></tr>
                            ) : (
                                filteredUsers.map(u => (
                                    <tr key={u.uid || u.email} className={`hover:bg-slate-50 transition-colors ${!u.isApproved ? 'bg-amber-50/30' : ''}`}>
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-slate-900">{u.name}</div>
                                            <div className="text-xs text-slate-500">{u.email}</div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-600">
                                            {new Date(u.joinedAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-slate-600">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 font-mono text-xs font-bold">
                                                {u.completedScenarioIds?.length || 0}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <button 
                                                onClick={() => toggleRole(u)}
                                                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-bold transition-colors ${
                                                    u.role === 'admin' 
                                                    ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' 
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                            >
                                                {u.role === 'admin' ? <Shield size={12} /> : null}
                                                {u.role === 'admin' ? 'Admin' : 'User'}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4">
                                            {u.isApproved ? (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 animate-pulse">
                                                    Pending
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            {u.email !== 'mkajanth@gmail.com' && (
                                                <button 
                                                    onClick={() => toggleApproval(u)}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1 ml-auto ${
                                                        u.isApproved 
                                                        ? 'bg-red-50 text-red-600 hover:bg-red-100' 
                                                        : 'bg-green-600 text-white hover:bg-green-700 shadow-md'
                                                    }`}
                                                >
                                                    {u.isApproved ? <XCircle size={14} /> : <CheckCircle size={14} />}
                                                    {u.isApproved ? 'Revoke' : 'Approve'}
                                                </button>
                                            )}
                                            {u.email === 'mkajanth@gmail.com' && (
                                                <span className="text-xs text-slate-400 italic">Super Admin</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>

        {/* Dojo Management */}
        <section>
            <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Brain size={20} /> Dojo Content Management
            </h3>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <p className="text-slate-600 mb-4">
                    Pre-generate Nunchi scenarios to populate the cache. Users will be served these cached scenarios randomly (excluding ones they've already seen).
                </p>
                <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Quantity</label>
                        <select 
                            value={genCount} 
                            onChange={(e) => setGenCount(Number(e.target.value))}
                            className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
                            disabled={isGenerating}
                        >
                            <option value="0">Select...</option>
                            <option value="1">1 Scenario</option>
                            <option value="5">5 Scenarios</option>
                            <option value="10">10 Scenarios</option>
                            <option value="20">20 Scenarios</option>
                        </select>
                    </div>
                    <button 
                        onClick={handleBatchGenerate}
                        disabled={isGenerating || genCount === 0}
                        className={`px-6 py-2 rounded-lg font-bold flex items-center gap-2 transition-all ${
                            isGenerating || genCount === 0
                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md'
                        }`}
                    >
                        {isGenerating ? <Loader className="animate-spin" size={18} /> : <Play size={18} />}
                        {isGenerating ? 'Generating...' : 'Start Batch Job'}
                    </button>
                    {genStatus && (
                        <div className={`text-sm font-medium ${genStatus.includes('Error') ? 'text-red-500' : 'text-emerald-600'}`}>
                            {genStatus}
                        </div>
                    )}
                </div>
            </div>
        </section>

        {/* API Logs */}
        <section>
           <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Database size={20} /> System Logs
           </h3>
           <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
             <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-xs">
                    <tr>
                      <th className="px-6 py-4">Function</th>
                      <th className="px-6 py-4">Model</th>
                      <th className="px-6 py-4 text-right">In / Out</th>
                      <th className="px-6 py-4 text-right">Cost</th>
                      <th className="px-6 py-4 text-right">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loading ? (
                       <tr><td colSpan={5} className="p-8 text-center text-slate-500">Loading logs from cloud...</td></tr>
                    ) : (
                        records.map((rec) => (
                        <tr key={rec.id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 font-medium text-slate-900">{rec.endpoint}</td>
                            <td className="px-6 py-4">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-800">
                                {rec.model.replace('gemini-', '')}
                            </span>
                            </td>
                            <td className="px-6 py-4 text-right text-slate-500">
                            {rec.inputTokens} <span className="text-slate-300">/</span> {rec.outputTokens}
                            </td>
                            <td className="px-6 py-4 text-right font-mono text-emerald-600">
                            ${rec.cost.toFixed(6)}
                            </td>
                            <td className="px-6 py-4 text-right text-slate-400 text-xs">
                            {new Date(rec.timestamp).toLocaleTimeString()}
                            </td>
                        </tr>
                        ))
                    )}
                  </tbody>
                </table>
             </div>
           </div>
        </section>

      </div>
    </div>
  );
};

export default AdminDashboard;